import { useAppData } from '@app/core/app-data';
import { useToast } from '@app/core/notifications';
import { Form, NullMessage, SwitchField, TextAreaField, TextField } from '@mi-front/ui';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Box, Button, Grid, Paper } from '@mui/material';
import { FieldArray } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';
import { apiClient } from '../../../../../api';
import { StationsInput } from '../../../../../components/stations/StationsInput';
import { useStations } from '../../../../../core/stations/hooks/useStations';

export function DescriptionFootersForm() {
  const { refetchStations } = useAppData();
  const stations = useStations();
  const [station, setStation] = useState(stations[0]);
  const { $t } = useIntl();
  const { notifySuccess } = useToast();

  const validationSchema = yup.object().shape({
    descriptionFooters: yup
      .array(
        yup.object().shape({
          name: yup.string().required(
            $t({
              id: 'common.required',
              defaultMessage: 'Requerido',
            }),
          ),
          content: yup.string().required(
            $t({
              id: 'common.required',
              defaultMessage: 'Requerido',
            }),
          ),
          default: yup.boolean().required(),
        }),
      )
      .test(
        'unique',
        $t({
          id: 'admin.fragments.twoDefaultsNotAllowed',
          defaultMessage: 'No puede haber dos footers establecidos como default',
        }),
        (values) => values.filter((f: { default: boolean }) => f.default).length <= 1,
      ),
  });

  return (
    <Box>
      <Grid container>
        <Grid item md={5}>
          <StationsInput stations={stations} value={station} onChange={setStation} />
        </Grid>
      </Grid>

      <Box>
        <h6>
          <FormattedMessage
            id="admin.fragments.registeredFootersTitle"
            defaultMessage="Footers registrados"
          />
          :
        </h6>
        <Form
          key={station.id}
          validationSchema={validationSchema}
          initialValues={{
            descriptionFooters: station.featuresConfig?.fragments?.descriptionFooters || [],
          }}
          onSubmit={async (data) => {
            await apiClient.fragments.configUpdate(station.id, {
              descriptionFooters: data.descriptionFooters,
            });

            notifySuccess(
              $t(
                {
                  id: 'admin.fragments.fragmentsConfigUpdated',
                  defaultMessage: 'Configuración de fragmentos para {stationName} actualizada',
                },
                { stationName: station.name },
              ),
            );

            refetchStations();
          }}
        >
          {({ values, errors }) => {
            return (
              <Box mb={4}>
                <FieldArray
                  name="descriptionFooters"
                  render={(arrayHelpers) => (
                    <div>
                      {!values.descriptionFooters?.length && (
                        <NullMessage>
                          <FormattedMessage
                            id="admin.fragments.noFooters"
                            defaultMessage="No hay footers registrados"
                          />
                        </NullMessage>
                      )}
                      {values.descriptionFooters?.map((friend, index) => (
                        <div key={index}>
                          <Paper sx={{ mb: 2 }}>
                            <Box p={2} display={'flex'}>
                              <Box mr={2}>{index + 1}.</Box>
                              <Box flexGrow={1}>
                                <Box>
                                  <Grid container>
                                    <Grid item md={5}>
                                      <TextField
                                        label={$t({
                                          id: 'common.text.name',
                                          defaultMessage: 'Nombre',
                                        })}
                                        name={`descriptionFooters.${index}.name`}
                                        markRequired
                                      />
                                    </Grid>
                                    <Grid item md={5} sx={{ pt: 4, pl: 2 }}>
                                      <SwitchField
                                        name={`descriptionFooters.${index}.default`}
                                        label={$t({
                                          id: 'admin.fragments.useAsDefault',
                                          defaultMessage: 'Usar como default',
                                        })}
                                        size={'sm'}
                                      />
                                    </Grid>
                                    <Grid item md={2} textAlign={'right'}>
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => arrayHelpers.remove(index)}
                                      >
                                        <FormattedMessage
                                          id="common.remove"
                                          defaultMessage="Eliminar"
                                        />
                                      </Button>
                                    </Grid>
                                  </Grid>
                                  <Grid container>
                                    <Grid item md={8}>
                                      <TextAreaField
                                        label={$t({
                                          id: 'common.text.content',
                                          defaultMessage: 'Contenido',
                                        })}
                                        name={`descriptionFooters.${index}.content`}
                                        rows={3}
                                        markRequired
                                      />
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Box>
                            </Box>
                          </Paper>
                        </div>
                      ))}

                      <Box mt={3}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() =>
                            arrayHelpers.insert(values.descriptionFooters.length, {
                              name: '',
                              content: '',
                              default: false,
                            })
                          }
                        >
                          <FormattedMessage
                            id="admin.fragments.addNewFooter"
                            defaultMessage="Agregar nuevo footer"
                          />
                        </Button>
                      </Box>
                    </div>
                  )}
                />
              </Box>
            );
          }}
        </Form>
      </Box>
    </Box>
  );
}
